/**
 * Deja arrastrar cualquier ventana modal por su cabecera.
 *
 * ## Por qué global y no modal por modal
 *
 * Hay 37 modales en la aplicación y la molestia es siempre la misma: la ventana
 * tapa justo el dato que hace falta para rellenarla --el arqueo tapa el listado
 * de pagos, el pago tapa las líneas de la factura--. Poner la capacidad en cada
 * una sería 37 cambios, y la 38.ª nacería sin ella.
 *
 * Esto se engancha una sola vez, al arrancar, y vale para todas: las de hoy y
 * las que vengan.
 *
 * ## Qué arrastra y qué no
 *
 * Sólo la **cabecera** (`.ant-modal-header`). El cuerpo no, porque ahí se
 * selecciona texto y se rellenan campos; y el aspa de cerrar tampoco, que vive
 * fuera de la cabecera.
 *
 * Las confirmaciones (`messageApi.confirm`) no tienen cabecera, así que se
 * quedan quietas. Es lo que se quiere: son de una sola respuesta y no tapan un
 * trabajo en curso.
 *
 * ## La posición se olvida al cerrar
 *
 * Si no, la siguiente ventana abriría descolocada sin que nadie la haya movido,
 * y eso se percibe como un fallo. Al ocultarse la modal se le quita el
 * desplazamiento, así que siempre abre centrada.
 */

const MARGEN = 24; // lo mínimo que queda a la vista al arrastrar hacia un borde

const posiciones = new WeakMap();

const aplicar = (modal, x, y) => {
  posiciones.set(modal, { x, y });
  modal.style.transform = x || y ? `translate(${x}px, ${y}px)` : "";
};

/** Que no se pueda esconder del todo fuera de la pantalla. */
const acotar = (modal, x, y) => {
  const caja = modal.getBoundingClientRect();
  const { x: ax = 0, y: ay = 0 } = posiciones.get(modal) || {};
  // Bordes del elemento sin el desplazamiento actual, para acotar en absoluto.
  const izquierda = caja.left - ax;
  const arriba = caja.top - ay;
  return {
    x: Math.min(
      Math.max(x, -izquierda + MARGEN - caja.width + MARGEN),
      window.innerWidth - izquierda - MARGEN
    ),
    y: Math.min(Math.max(y, -arriba + MARGEN), window.innerHeight - arriba - MARGEN),
  };
};

/**
 * Engancha el arrastre en todo el documento. Devuelve la función que lo suelta.
 *
 * @param {Document} doc
 * @returns {() => void}
 */
export const activarModalesArrastrables = (doc = document) => {
  let modal = null;
  let inicio = null;

  const alPulsar = (evento) => {
    if (evento.button !== 0) return;
    const cabecera = evento.target.closest?.(".ant-modal-header");
    if (!cabecera) return;
    // Un botón dentro de la cabecera se pulsa, no arrastra.
    if (evento.target.closest("button, a, input, select, textarea")) return;

    modal = cabecera.closest(".ant-modal");
    if (!modal) return;

    const { x = 0, y = 0 } = posiciones.get(modal) || {};
    inicio = { raton: { x: evento.clientX, y: evento.clientY }, modal: { x, y } };
    cabecera.setPointerCapture?.(evento.pointerId);
    // Sin esto el navegador selecciona el texto del título al arrastrar.
    evento.preventDefault();
  };

  const alMover = (evento) => {
    if (!modal || !inicio) return;
    const destino = acotar(
      modal,
      inicio.modal.x + (evento.clientX - inicio.raton.x),
      inicio.modal.y + (evento.clientY - inicio.raton.y)
    );
    aplicar(modal, destino.x, destino.y);
  };

  const alSoltar = () => {
    modal = null;
    inicio = null;
  };

  doc.addEventListener("pointerdown", alPulsar, true);
  doc.addEventListener("pointermove", alMover, true);
  doc.addEventListener("pointerup", alSoltar, true);
  doc.addEventListener("pointercancel", alSoltar, true);

  // Al cerrarse una modal, antd oculta su envoltorio. Ahí se le quita el
  // desplazamiento para que la próxima vez abra centrada.
  const vigilante =
    typeof MutationObserver === "function"
      ? new MutationObserver((cambios) => {
          for (const cambio of cambios) {
            const envoltorio = cambio.target;
            if (!envoltorio.classList?.contains("ant-modal-wrap")) continue;
            if (envoltorio.style.display !== "none") continue;
            const oculta = envoltorio.querySelector(".ant-modal");
            if (oculta) aplicar(oculta, 0, 0);
          }
        })
      : null;
  vigilante?.observe(doc.body, {
    attributes: true,
    attributeFilter: ["style"],
    subtree: true,
  });

  return () => {
    doc.removeEventListener("pointerdown", alPulsar, true);
    doc.removeEventListener("pointermove", alMover, true);
    doc.removeEventListener("pointerup", alSoltar, true);
    doc.removeEventListener("pointercancel", alSoltar, true);
    vigilante?.disconnect();
  };
};
